'use client';

/**
 * Optional center content for the topbar. When set (e.g. by editor on mobile),
 * DefaultTopBar renders it in the center instead of config centerContent.
 * Used to merge topbar and doc header into one row on editor mobile.
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

type TopBarCenterContentContextValue = {
  centerContent: ReactNode | null;
  setCenterContent: (content: ReactNode | null) => void;
};

const TopBarCenterContentContext = createContext<TopBarCenterContentContextValue | null>(null);

export function useTopBarCenterContent(): TopBarCenterContentContextValue {
  const ctx = useContext(TopBarCenterContentContext);
  if (!ctx) return { centerContent: null, setCenterContent: () => {} };
  return ctx;
}

export function TopBarCenterContentProvider({ children }: { children: ReactNode }) {
  const [centerContent, setCenterContentState] = useState<ReactNode | null>(null);
  const setCenterContent = useCallback((content: ReactNode | null) => {
    setCenterContentState(() => content);
  }, []);
  return (
    <TopBarCenterContentContext.Provider value={{ centerContent, setCenterContent }}>
      {children}
    </TopBarCenterContentContext.Provider>
  );
}

export { TopBarCenterContentContext };
